import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';
import api from '../config/api';
import './common.css';
import './ProductList.css';

function AddProduct() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        ebay_id: '',
        ebay_variation_sku: '',
        amazon_sku: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!formData.ebay_id.trim() || !formData.amazon_sku.trim()) {
            setError('eBay ID and Amazon SKU are required');
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/api/products', {
                ebay_id: formData.ebay_id.trim(),
                ebay_variation_sku: formData.ebay_variation_sku.trim() || undefined,
                amazon_sku: formData.amazon_sku.trim()
            });

            if (response.data.success) {
                setSuccess('Product created successfully!');
                setTimeout(() => {
                    navigate('/products');
                }, 1500);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
                navigate('/login');
            } else {
                const errorData = err.response?.data;
                let errorMessage = errorData?.error || 'Failed to create product';

                // If there are missing fields or additional details, include them in the error message
                if (errorData?.details) {
                    errorMessage += `: ${errorData.details}`;
                } else if (errorData?.missingFields && errorData.missingFields.length > 0) {
                    errorMessage += `. Missing fields: ${errorData.missingFields.join(', ')}`;
                }

                setError(errorMessage);
            }
            console.error('Error creating product:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/products');
    };

    return (
        <div className="page-container">
            <Navigation />

            <div className="page-content">
                <div className="page-header">
                    <h2>Add New Product</h2>
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/products')}
                    >
                        ← Back to Products
                    </button>
                </div>
            </div>

            <div className="form-card">
                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                <form onSubmit={handleSubmit} className="product-form">
                    <div className="form-group">
                        <label htmlFor="ebay_id">
                            eBay ID <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="ebay_id"
                            name="ebay_id"
                            value={formData.ebay_id}
                            onChange={handleChange}
                            placeholder="Enter eBay product ID"
                            disabled={loading}
                            required
                        />
                        <small className="help-text">
                            The unique eBay product identifier
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="ebay_variation_sku">
                            eBay Variation SKU
                        </label>
                        <input
                            type="text"
                            id="ebay_variation_sku"
                            name="ebay_variation_sku"
                            value={formData.ebay_variation_sku}
                            onChange={handleChange}
                            placeholder="Enter eBay variation SKU (optional)"
                            disabled={loading}
                        />
                        <small className="help-text">
                            Optional: For products with variations (e.g., size, color)
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="amazon_sku">
                            Amazon SKU <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="amazon_sku"
                            name="amazon_sku"
                            value={formData.amazon_sku}
                            onChange={handleChange}
                            placeholder="Enter Amazon SKU"
                            disabled={loading}
                            required
                        />
                        <small className="help-text">
                            Your Amazon product SKU identifier
                        </small>
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Creating...' : 'Create Product'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                    </div>
                </form>

                <div className="form-info">
                    <h3>ℹ️ Information</h3>
                    <ul>
                        <li>The system will automatically fetch product details from eBay API</li>
                        <li>Product information like title, price, and quantity will be retrieved automatically</li>
                        <li>Make sure the eBay ID is valid and accessible</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default AddProduct;
